import Cookies from 'js-cookie';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFirestoreConnect, WhereOptions } from 'react-redux-firebase';
import { Outlet } from 'react-router-dom';
import { SiteFooter } from '../components/SiteFooter';
import { SiteHeader } from '../components/SiteHeader';
import { CountryMarketController } from '../controllers/CountryMarketController';
import { useCountryMarketState } from '../controllers/CountryMarketController/CountryMarketState';
import { CustomerSelectionBar } from '../features/CustomerUserSelection/CustomerSelectionBar';
import { useAuth } from '../hooks/useAuth';
import { useGTM } from '../hooks/useGTM';
import { useIngredientsHandler } from '../hooks/useIngredientsHandler';
import { useLocalizationCookieHandler } from '../hooks/useLocalizationCookieHandler';
import { formatWindowSizeForGTM, useWindowSize } from '../shared/useWindowSize';
import { TrackingSchema } from '../types';

const AppLayout = () => {
  const [{ displayUser, auth, customerId }] = useAuth();
  const { i18n } = useTranslation();
  const windowSize = useWindowSize();
  const [{ countryMarketConfig }] = useCountryMarketState();
  const { changeAppLanguage } = useLocalizationCookieHandler();
  const { updateDataLayer, initialiseDataLayer } = useGTM();
  const initialLanguageFromCP = countryMarketConfig?.languages[0];
  const languageFromCookie = Cookies.get('localization')
  const isAuthenticated = !!auth.uid;

  const customersNotEmpty =
    displayUser &&
    displayUser.customers &&
    !displayUser.customers.includes(null) &&
    !displayUser.customers.includes(undefined);

  useFirestoreConnect(() => [
    ...(isAuthenticated
      ? [
          {
            collection: 'users',
            doc: displayUser?.uid || auth.uid,
            subcollections: [{ collection: 'briefings' }],
            storeAs: 'briefings',
          },
          {
            collection: 'users',
            doc: auth.uid,
            storeAs: 'user',
          },
        ]
      : []),
    ...(customersNotEmpty
      ? [
          {
            collection: 'customers',
            // `__name__` is a magic string to query for the UID
            where: ['__name__', 'in', displayUser.customers] as WhereOptions,
          },
          {
            collection: 'customers',
            // lookup a non existing erp_id, if the user has none
            doc: customerId || 'give_us_an_empty_object',
            subcollections: [{ collection: 'briefings' }],
            storeAs: 'customerBriefings',
          },
        ]
      : []),
  ]);

  useIngredientsHandler();

  useEffect(() => {
    // If we already have a language cookie set, don't continue
    if(languageFromCookie) {
      i18n.changeLanguage(languageFromCookie);
      return;
    }

    // If the user is coming from the link on the Customer Portal
    // but is not logged in yet then the locale will not be set.
    // We have the country-market config in state from the CP
    // so we manually pass the language in that to the locale setter.
    if (initialLanguageFromCP) {
      changeAppLanguage(initialLanguageFromCP);
    } else {
      changeAppLanguage().catch(() => {
        console.log(
          'Unable to set language from cookie, so defaulting to en_US',
        );

        changeAppLanguage('en_US');
      });
    }
  }, [changeAppLanguage, i18n, initialLanguageFromCP, languageFromCookie]);

  useEffect(() => {
    // Initialise GTM
    if (window.dataLayer) return;

    const isWindowSizeComputed = windowSize.reduce((a, b) => a + b, 0) !== 0;

    if (!isWindowSizeComputed) return;

    const initData = {
      language: i18n.language,
      environment: process.env.REACT_APP_ENVIRONMENT || 'local',
      viewport: formatWindowSizeForGTM([window.innerWidth, window.innerHeight]),
      marketSegment: countryMarketConfig?.taxonomyMarketAbbreviaton || '',
      country: countryMarketConfig?.datalakeCountryAbbreviation || '',
    };

    initialiseDataLayer({
      event: 'Init',
      schema: TrackingSchema.site,
      data: initData,
    });
  }, [
    countryMarketConfig,
    i18n,
    windowSize,
    initialiseDataLayer,
    updateDataLayer,
  ]);

  return (
    <div className="flex-col flex-grow">
      <div className="flex flex-col w-full h-screen lg:flex-row">
        <SiteHeader />
        <main className="flex flex-col flex-grow md:overflow-y-auto">
          <CustomerSelectionBar />
          <div className="relative flex flex-col flex-grow flex-shrink-0">
            <CountryMarketController>
              <Outlet />
            </CountryMarketController>
          </div>
          <SiteFooter />
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
